import type { Precision } from "./types";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * The only place a date becomes words.
 *
 * A date in this archive carries the precision it was actually known to. A
 * photograph the scanner dated to the decade is a photograph dated to the
 * decade; rendering "1 January 1950" for it invents six digits of certainty
 * that nobody has, and once it is on screen there is no way for a reader to
 * tell it apart from an EXIF timestamp. So the precision decides how much of
 * the string is shown, and anything short of `day` is never spelled out to the
 * day. Every view calls this — there is no second date formatter in the client.
 *
 * `at` may be a full ISO timestamp or a truncated one ("1950", "1950-03"),
 * which is what the bucket keys from `/dates` and `/timeline` are.
 */
export function formatDate(
  at: string | null | undefined,
  precision: Precision | null | undefined,
  opts: { short?: boolean } = {},
): string {
  if (!at) return opts.short ? "undated" : "Date unknown";

  const year = at.slice(0, 4);
  if (!/^\d{4}$/.test(year)) return opts.short ? "undated" : "Date unknown";
  const month = Number(at.slice(5, 7));
  const day = Number(at.slice(8, 10));
  const decade = `${year.slice(0, 3)}0`;

  switch (precision) {
    case "decade":
      return `${decade}s`;

    case "circa":
      // "circa" is a year that somebody estimated. Say so in the string; the
      // reader cannot see the column it came from.
      return opts.short ? `c. ${year}` : `around ${year}`;

    case "year":
      return year;

    case "month":
      if (!month || month < 1 || month > 12) return year;
      return opts.short ? `${MON[month - 1]} ${year}` : `${MONTHS[month - 1]} ${year}`;

    case "day":
      if (!month || !day) return year;
      return opts.short
        ? `${day} ${MON[month - 1]} ${year}`
        : `${day} ${MONTHS[month - 1]} ${year}`;

    case "unknown":
    case null:
    case undefined:
    default:
      // A timestamp with no recorded precision is a timestamp of unknown
      // trustworthiness. Show the year and admit it, rather than picking the
      // most precise reading the string happens to allow.
      return opts.short ? `${year}?` : `${year} — precision unrecorded`;
  }
}

/** The precision itself, as a short badge. Null and "unknown" are the same thing. */
export function precisionLabel(p: Precision | null | undefined): string {
  return p && p !== "unknown" ? p : "unknown";
}

/** True when the date is a guess rather than a reading off the file. */
export function isFuzzy(p: Precision | null | undefined): boolean {
  return p === "decade" || p === "circa" || p === "unknown" || p == null;
}

/** A bucket key from `/dates` or `/timeline`, rendered through `formatDate`. */
export function bucketLabel(key: string, precision: Precision | null): string {
  if (!key || key === "unknown") return "Date unknown";
  return formatDate(key, precision);
}

/** Video runtime. `mm:ss`, or `h:mm:ss` once it earns the hour. */
export function duration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "";
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** A seek position, always mm:ss so a strip of them lines up. */
export function timecode(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function bytes(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(1)} GB`;
  if (n >= 1 << 20) return `${Math.round(n / (1 << 20))} MB`;
  if (n >= 1 << 10) return `${Math.round(n / (1 << 10))} kB`;
  return `${n} B`;
}

export function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** A person with no name yet is still a person; never render "null". */
export function personName(name: string | null | undefined): string {
  return name && name.trim() ? name : "Unnamed";
}

export function lifespan(birth: number | null, death: number | null): string {
  if (birth && death) return `${birth} – ${death}`;
  if (birth) return `b. ${birth}`;
  if (death) return `d. ${death}`;
  return "dates unknown";
}
